import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { QuestSDK } from "@bandit-network/quest-sdk";
import { ClusterBanditType, ClusterBandit } from "@bandit-network/sdk-api";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

interface BanditQuestContextType {
  apiKey: string;
  cluster: ClusterBanditType;
  getSignatureMessage: (address: string) => Promise<{
    message: string;
    expireBy: string;
  }>;
  questSDK: QuestSDK;
  walletSettings?: WalletSettingOverride | undefined;
  closeOnSuccess?: boolean;
  address?: string;
}

const queryClient = new QueryClient();

const BanditQuestContext = createContext<BanditQuestContextType | null>(null);

interface WalletSettings {
  inheritEvmWallets?: boolean;
  inheritSolWallets?: boolean;
  customSignature?: boolean;
  signature?: string;
  onConnectClick?: () => void;
}

interface WalletSettingOverride extends WalletSettings {
  setSignature: (signature: string) => void;
}

interface BanditContextProps {
  apiKey: string;
  cluster: ClusterBanditType;
  walletSettings?: WalletSettings | undefined;
  children: React.ReactNode;
  closeOnSuccess?: boolean;
  address?: string;
}

export const BanditContextProvider: React.FC<BanditContextProps> = ({
  apiKey,
  cluster = ClusterBandit.Mainnet,
  walletSettings,
  children,
  closeOnSuccess,
  address,
}) => {
  const [signature, setSignature] = useState<string>("");
  const questSDK = new QuestSDK(apiKey, cluster);
  const getSignatureMessage = useCallback(async (address: string) => {
    const { nonce, expBy } = await questSDK.getNonce(address);
    return {
      message: `Verify your account: ${address}\nNonce: ${nonce}`,
      expireBy: expBy,
    };
  }, []);

  const value = useMemo(() => {
    return {
      apiKey,
      cluster,
      getSignatureMessage,
      questSDK,
      walletSettings: {
        ...walletSettings,
        signature: walletSettings?.signature || signature,
        setSignature,
      },
      closeOnSuccess,
      address,
    };
  }, [
    apiKey,
    cluster,
    getSignatureMessage,
    questSDK,
    walletSettings,
    signature,
    setSignature,
    closeOnSuccess,
    address,
  ]);

  return (
    <div data-img-src="true">
      <BanditQuestContext.Provider value={value}>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </BanditQuestContext.Provider>
    </div>
  );
};

export const useBanditContext = (): BanditQuestContextType =>
  useContext(BanditQuestContext);
